/**
 * Tanjong Life · the self-account question, asked of the Claims Integrity Sentinel — the
 * agent commissioned on stage.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `claims-integrity-sentinel`. This one is
 * asked minutes after it was born, so it answers in elapsed time rather than in dates: RULE
 * R-TOK says only deployProcess, deployAgent, bootAgent and runCase resolve story tokens, and
 * a scenario that authored one would print the raw token on the screen. Saying "since I was
 * commissioned" is both safer and truer than a date the room watched being created.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveIntegrity: Scenario = {
  id: 'tanjong-chat-improve-integrity',
  orgId: 'tanjong-life',
  agentPk: 'claims-integrity-sentinel',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Twice since I was commissioned, and neither came from an instruction. The first was the payee-change finding: the window between a settlement being authorised and the money leaving is owned by no activity in workflow 05. I took it. I re-read the payee daily on everything authorised and not yet released, and on any change I open a case at the treasury lead with both records attached. I never hold the payment — holding it is not mine.',
      sources: [
        'Memory · after the payee-change finding',
        'Probe · Payee change after authorisation · daily',
      ],
      delay: 1200,
    },
    {
      do: 'say',
      text: 'The second was withdrawing my own work. I had a referral-rate drift probe running on a window that turned out to be confounded — the indicator set had been re-issued inside it, so any slope I published would have been partly my own measurement moving. I halted it rather than publishing it with a caveat, because a caveat printed under a slope gets read as a slope.',
      sources: ['Memory · after the drift probe was halted'],
      delay: 1300,
    },
    {
      do: 'insight',
      title: 'What I have not improved, and will not claim',
      body: 'Coverage of the windows nobody owns. I score 56 out of 100 on it. I found one such window and took it; I have no way to enumerate the others, because there is no register of what the workflow does not do — I find them by tripping over them. So 56 is not a number I can move by trying harder, and I would rather it sat on my record at 56 than be quietly dropped from the set. My overall evaluation is 88, up six.',
      sources: [
        'Evaluation · 88 overall · Coverage of the windows nobody owns 56',
        'Probes · 4 built, 1 halted',
      ],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open my decision stream',
      href: '/agents/claims-integrity-sentinel',
      delay: 600,
    },
  ],
};
